import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Spinner } from 'react-bootstrap';
import axiosInstance, { logoutUser } from '../../api/api';
import { API_ROUTES } from '../../config/api-routes';
import { useSessionStore } from '../../store/sessionStore';
import { normalizeUrl } from '../../utils/url';
import { toast } from 'react-toastify';
import { getUserFacingError } from '../../utils/userFacingError';
import { syncCurrentUserFromApi } from '../../utils/userProfile';
import { useCreatePromptDocumentUploads } from '../Create-Prompt/useCreatePromptDocumentUploads';
import { useCreatePromptVideoUploads } from '../Create-Prompt/useCreatePromptVideoUploads';
import ReviewStage from './ReviewStage';
import LinkedInStage from './LinkedInStage';
import WelcomeStage from './WelcomeStage';
import ResumeStage, { formatResumeSize } from './ResumeStage';
import OnboardingNav from './OnboardingNav';
import { buildSaveDraftFormData, isPersistedMediaUrl, promptItemsFromDraft } from './draftPayload';
import { cacheRolePromptsFromTexts } from '../Prompts/promptsV2RolePrompts';
import { resolveOnboardingRoleMeta } from './onboardingRolePromptMeta';
import type {
  OnboardingDraft,
  OnboardingDraftResponse,
  OnboardingStep,
  ScreeningPromptItem,
} from './types';
import './FastOnboarding.css';
import './FastOnboardingReview.css';
import '../Create-Prompt/Create-Prompt.css';
import '../Prompts/PromptsV2.css';

const ALLOWED_RESUME_EXTENSIONS = new Set(['pdf', 'docx']);
const MAX_FILE_SIZE = 10 * 1024 * 1024;

const STEP_ORDER: Record<OnboardingStep, number> = {
  welcome: 0,
  resume: 1,
  linkedin: 2,
  review: 3,
};

function isAllowedResume(file: File): boolean {
  const ext = (file.name || '').split('.').pop()?.toLowerCase() || '';
  return ALLOWED_RESUME_EXTENSIONS.has(ext);
}

function buildInitials(
  draft: OnboardingDraft | null,
  user: Record<string, unknown> | null
): string {
  const first = String(draft?.firstName || user?.firstName || user?.fname || '').trim();
  const last = String(draft?.lastName || user?.lastName || user?.lname || '').trim();
  if (first || last) {
    return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();
  }
  const email = String(user?.email || '').trim();
  return email ? email.charAt(0).toUpperCase() : 'SS';
}

export default function FastOnboarding() {
  const navigate = useNavigate();
  const [step, setStep] = useState<OnboardingStep>('welcome');
  const [furthestStep, setFurthestStep] = useState<OnboardingStep>('welcome');
  const [loadingDraft, setLoadingDraft] = useState(true);
  const [parsing, setParsing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [resumeName, setResumeName] = useState('');
  const [resumeUrl, setResumeUrl] = useState('');
  const [resumeSizeBytes, setResumeSizeBytes] = useState<number | null>(null);
  const [draft, setDraft] = useState<OnboardingDraft | null>(null);
  const [linkedin, setLinkedin] = useState('');
  const [targetRole, setTargetRole] = useState('');
  const [promptItems, setPromptItems] = useState<ScreeningPromptItem[]>([]);
  const [videoFiles, setVideoFiles] = useState<Record<number, File>>({});
  const [documentFiles, setDocumentFiles] = useState<Record<number, File>>({});
  const promptItemsRef = useRef(promptItems);
  promptItemsRef.current = promptItems;
  const persistedMediaKeyRef = useRef('');
  const mediaPersistTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const {
    videoUrls,
    videoUploads,
    clearVideoUploadState,
    clearAllVideoUploadState,
    retryVideoUpload,
    cancelVideoUpload,
    hasPendingVideoUploads,
    hasVideoUploadErrors,
    replaceVideoUrls,
    seedVideoUrls,
  } = useCreatePromptVideoUploads(videoFiles);

  const {
    documentUrls,
    documentUploads,
    clearDocumentUploadState,
    retryDocumentUpload,
    cancelDocumentUpload,
    hasPendingDocumentUploads,
    hasDocumentUploadErrors,
    replaceDocumentUrls,
    seedDocumentUrls,
  } = useCreatePromptDocumentUploads(documentFiles);

  const sessionUser = useSessionStore((state) => state.currentUser) as Record<
    string,
    unknown
  > | null;
  const initials = useMemo(() => buildInitials(draft, sessionUser), [draft, sessionUser]);

  const goToStep = useCallback((next: OnboardingStep) => {
    setStep(next);
    setFurthestStep((prev) => (STEP_ORDER[next] > STEP_ORDER[prev] ? next : prev));
  }, []);

  const handleStepSelect = useCallback(
    (next: OnboardingStep) => {
      if (STEP_ORDER[next] > STEP_ORDER[furthestStep]) return;
      if ((next === 'review' || next === 'linkedin') && !draft) return;
      setStep(next);
    },
    [draft, furthestStep]
  );

  const restoreDraftState = useCallback(
    (data: OnboardingDraftResponse) => {
      if (!data.draft) return;
      const items = promptItemsFromDraft(data.draft, data.screeningPromptItems);
      setDraft(data.draft);
      setResumeName(data.resumeFileName || '');
      setResumeUrl(data.resumeUrl || '');
      setResumeSizeBytes(null);
      setLinkedin(String(data.linkedin || data.draft.contact?.linkedin || ''));
      setTargetRole(String(data.draft.targetRole || data.draft.currentJobTitle || ''));
      setPromptItems(items);
      const roleMeta = resolveOnboardingRoleMeta(
        String(data.draft.targetRole || data.draft.currentJobTitle || ''),
        data.draft,
        useSessionStore.getState().currentUser as Record<string, unknown> | null
      );
      const rolePool = Array.isArray(data.draft.roleSpecificPrompts)
        ? data.draft.roleSpecificPrompts
        : [];
      if (rolePool.length > 0) {
        cacheRolePromptsFromTexts(
          roleMeta,
          rolePool.map((item) => String(item || ''))
        );
      }

      const nextVideoUrls: Record<number, string> = {};
      const nextDocumentUrls: Record<number, string> = {};
      items.forEach((item, index) => {
        const fileUrl = (item.fileUrl || '').trim();
        if (fileUrl && fileUrl !== 'N/A') {
          nextVideoUrls[index] = fileUrl;
        }
        const docUrl = (item.supportedDocumentUrl || '').trim();
        if (docUrl) {
          nextDocumentUrls[index] = docUrl;
        }
      });
      replaceVideoUrls(nextVideoUrls);
      replaceDocumentUrls(nextDocumentUrls);
      setVideoFiles({});
      setDocumentFiles({});
      persistedMediaKeyRef.current = JSON.stringify({
        videos: Object.entries(nextVideoUrls)
          .filter(([, url]) => isPersistedMediaUrl(url))
          .sort(([a], [b]) => Number(a) - Number(b)),
        docs: Object.entries(nextDocumentUrls)
          .filter(([, url]) => isPersistedMediaUrl(url))
          .sort(([a], [b]) => Number(a) - Number(b)),
      });
      setFurthestStep('review');
      setStep('review');
    },
    [replaceDocumentUrls, replaceVideoUrls]
  );

  // Keep prompt item media URLs in sync with completed direct uploads.
  useEffect(() => {
    setPromptItems((prev) => {
      let changed = false;
      const next = prev.map((item, index) => {
        const videoUrl = videoUrls[index];
        const documentUrl = documentUrls[index];
        const patch: Partial<ScreeningPromptItem> = {};
        if (isPersistedMediaUrl(videoUrl) && item.fileUrl !== videoUrl) {
          patch.fileUrl = videoUrl;
        }
        if (isPersistedMediaUrl(documentUrl) && item.supportedDocumentUrl !== documentUrl) {
          patch.supportedDocumentUrl = documentUrl;
        }
        if (Object.keys(patch).length === 0) {
          return item;
        }
        changed = true;
        return { ...item, ...patch };
      });
      if (changed) {
        promptItemsRef.current = next;
      }
      return changed ? next : prev;
    });

    // Drop local Files once S3 public URLs exist so the UI stops using blob: previews.
    setVideoFiles((prev) => {
      let changed = false;
      const next = { ...prev };
      Object.entries(videoUrls).forEach(([key, url]) => {
        const index = Number(key);
        if (isPersistedMediaUrl(url) && next[index]) {
          delete next[index];
          changed = true;
        }
      });
      return changed ? next : prev;
    });
    setDocumentFiles((prev) => {
      let changed = false;
      const next = { ...prev };
      Object.entries(documentUrls).forEach(([key, url]) => {
        const index = Number(key);
        if (isPersistedMediaUrl(url) && next[index]) {
          delete next[index];
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [videoUrls, documentUrls]);

  const loadSavedDraft = useCallback(async () => {
    setLoadingDraft(true);
    try {
      const { data } = await axiosInstance.get<OnboardingDraftResponse>(
        API_ROUTES.ONBOARDING.DRAFT
      );
      if (data?.hasDraft && data.draft) {
        restoreDraftState(data);
      }
    } catch {
      /* first visit */
    } finally {
      setLoadingDraft(false);
    }
  }, [restoreDraftState]);

  useEffect(() => {
    void loadSavedDraft();
  }, [loadSavedDraft]);

  const saveDraft = useCallback(
    async (
      nextStep: OnboardingStep,
      options?: {
        silent?: boolean;
        allowPendingUploads?: boolean;
        draftOverride?: OnboardingDraft;
        targetRoleOverride?: string;
        successMessage?: string;
        promptItemsOverride?: ScreeningPromptItem[];
      }
    ) => {
      const draftToSave = options?.draftOverride ?? draft;
      const roleToSave = options?.targetRoleOverride ?? targetRole;
      if (!draftToSave) return false;

      const allowPending = Boolean(options?.allowPendingUploads);
      if (!allowPending && (hasPendingVideoUploads() || hasPendingDocumentUploads())) {
        if (!options?.silent) {
          toast.info('Please wait for media uploads to finish.');
        }
        return false;
      }
      if (!allowPending && (hasVideoUploadErrors() || hasDocumentUploadErrors())) {
        if (!options?.silent) {
          toast.error('Fix failed media uploads before saving.');
        }
        return false;
      }

      const sourceItems = options?.promptItemsOverride ?? promptItemsRef.current;
      const itemsToSave = sourceItems.map((item, index) => ({
        ...item,
        fileUrl: isPersistedMediaUrl(videoUrls[index] || item.fileUrl)
          ? String(videoUrls[index] || item.fileUrl)
          : '',
        supportedDocumentUrl: isPersistedMediaUrl(documentUrls[index] || item.supportedDocumentUrl)
          ? String(documentUrls[index] || item.supportedDocumentUrl)
          : '',
      }));

      if (!options?.silent) {
        setSaving(true);
      }
      setError('');
      try {
        const formData = buildSaveDraftFormData({
          draft: draftToSave,
          linkedin: linkedin.trim() ? normalizeUrl(linkedin.trim()) : '',
          targetRole: roleToSave,
          step: nextStep,
          promptItems: itemsToSave,
          videoFiles: {},
          documentFiles: {},
          videoUrls,
          documentUrls,
        });
        const { data } = await axiosInstance.post(API_ROUTES.ONBOARDING.SAVE_DRAFT, formData);
        if (!data?.success) {
          throw new Error(data?.message || 'Could not save your progress.');
        }
        if (data.draft) {
          setDraft(data.draft as OnboardingDraft);
        }
        if (data.screeningPromptItems) {
          const savedItems = data.screeningPromptItems as ScreeningPromptItem[];
          const mergedItems = savedItems.map((item, index) => {
            const local = promptItemsRef.current[index];
            const fileUrl = isPersistedMediaUrl(videoUrls[index])
              ? String(videoUrls[index])
              : isPersistedMediaUrl(item.fileUrl)
                ? String(item.fileUrl)
                : isPersistedMediaUrl(local?.fileUrl)
                  ? String(local?.fileUrl)
                  : '';
            const supportedDocumentUrl = isPersistedMediaUrl(documentUrls[index])
              ? String(documentUrls[index])
              : isPersistedMediaUrl(item.supportedDocumentUrl)
                ? String(item.supportedDocumentUrl)
                : isPersistedMediaUrl(local?.supportedDocumentUrl)
                  ? String(local?.supportedDocumentUrl)
                  : '';
            return {
              ...item,
              description: local?.description || item.description || '',
              fileUrl,
              supportedDocumentUrl,
            };
          });
          promptItemsRef.current = mergedItems;
          setPromptItems(mergedItems);

          const nextVideoUrls: Record<number, string> = {};
          const nextDocumentUrls: Record<number, string> = {};
          mergedItems.forEach((item, index) => {
            if (isPersistedMediaUrl(item.fileUrl)) nextVideoUrls[index] = String(item.fileUrl);
            if (isPersistedMediaUrl(item.supportedDocumentUrl)) {
              nextDocumentUrls[index] = String(item.supportedDocumentUrl);
            }
          });

          if (allowPending) {
            // Don't reset in-flight upload trackers mid-upload.
            seedVideoUrls(nextVideoUrls);
            seedDocumentUrls(nextDocumentUrls);
          } else {
            replaceVideoUrls(nextVideoUrls);
            replaceDocumentUrls(nextDocumentUrls);
            setVideoFiles({});
            setDocumentFiles({});
          }
        }
        if (data.user) {
          syncCurrentUserFromApi(data.user as Record<string, unknown>);
        }
        if (!options?.silent) {
          toast.success(options?.successMessage || 'Progress saved.');
        }
        return true;
      } catch (err: unknown) {
        const message = getUserFacingError(err, 'Could not save your progress. Please try again.');
        setError(message);
        if (!options?.silent) {
          toast.error(message);
        }
        return false;
      } finally {
        if (!options?.silent) {
          setSaving(false);
        }
      }
    },
    [
      draft,
      linkedin,
      targetRole,
      videoUrls,
      documentUrls,
      hasPendingVideoUploads,
      hasPendingDocumentUploads,
      hasVideoUploadErrors,
      hasDocumentUploadErrors,
      replaceVideoUrls,
      replaceDocumentUrls,
      seedVideoUrls,
      seedDocumentUrls,
    ]
  );

  // After S3 uploads finish, silently persist URLs into the onboarding draft.
  useEffect(() => {
    if (loadingDraft || !draft || step !== 'review') return;

    const mediaSnapshot = {
      videos: Object.entries(videoUrls)
        .filter(([, url]) => isPersistedMediaUrl(url))
        .sort(([a], [b]) => Number(a) - Number(b)),
      docs: Object.entries(documentUrls)
        .filter(([, url]) => isPersistedMediaUrl(url))
        .sort(([a], [b]) => Number(a) - Number(b)),
    };
    if (mediaSnapshot.videos.length === 0 && mediaSnapshot.docs.length === 0) return;

    const mediaKey = JSON.stringify(mediaSnapshot);
    if (mediaKey === persistedMediaKeyRef.current) return;

    if (mediaPersistTimerRef.current) {
      clearTimeout(mediaPersistTimerRef.current);
    }
    mediaPersistTimerRef.current = setTimeout(() => {
      void saveDraft('review', {
        silent: true,
        allowPendingUploads: true,
      }).then((ok) => {
        if (ok) {
          persistedMediaKeyRef.current = mediaKey;
        }
      });
    }, 400);

    return () => {
      if (mediaPersistTimerRef.current) {
        clearTimeout(mediaPersistTimerRef.current);
      }
    };
  }, [videoUrls, documentUrls, draft, loadingDraft, step, saveDraft]);

  const persistPromptDetails = useCallback(async () => {
    if (!draft || step !== 'review') return false;
    return saveDraft('review', {
      silent: true,
      allowPendingUploads: true,
    });
  }, [draft, step, saveDraft]);

  const persistDraftChanges = useCallback(
    async (
      changes: { draftPatch?: Partial<OnboardingDraft>; targetRole?: string },
      options?: { successMessage?: string }
    ): Promise<boolean> => {
      if (!draft) return false;
      const nextDraft = changes.draftPatch ? { ...draft, ...changes.draftPatch } : draft;
      const nextTargetRole = changes.targetRole ?? targetRole;

      setDraft(nextDraft);
      if (changes.targetRole !== undefined) {
        setTargetRole(nextTargetRole);
      }

      return saveDraft('review', {
        draftOverride: nextDraft,
        targetRoleOverride: nextTargetRole,
        successMessage: options?.successMessage,
      });
    },
    [draft, targetRole, saveDraft]
  );

  const parseResume = useCallback(async (file: File) => {
    setError('');
    setParsing(true);
    try {
      const formData = new FormData();
      formData.append('resume', file);
      const { data } = await axiosInstance.post(API_ROUTES.ONBOARDING.PARSE_RESUME, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 90000,
      });
      if (!data?.success || !data?.draft) {
        throw new Error(data?.message || 'Could not parse resume.');
      }
      setDraft(data.draft as OnboardingDraft);
      setResumeName(file.name);
      setResumeSizeBytes(file.size);
      setResumeUrl(String(data.resumeUrl || ''));
      setTargetRole(String(data.draft.targetRole || data.draft.currentJobTitle || ''));
      setLinkedin(String(data.linkedin || data.draft.contact?.linkedin || ''));
      setPromptItems(promptItemsFromDraft(data.draft));
      const roleMeta = resolveOnboardingRoleMeta(
        String(data.draft.targetRole || data.draft.currentJobTitle || ''),
        data.draft as OnboardingDraft,
        useSessionStore.getState().currentUser as Record<string, unknown> | null
      );
      const rolePool = Array.isArray(data.draft.roleSpecificPrompts)
        ? data.draft.roleSpecificPrompts
        : [];
      if (rolePool.length > 0) {
        cacheRolePromptsFromTexts(
          roleMeta,
          rolePool.map((item: unknown) => String(item || ''))
        );
      }
      setFurthestStep((prev) => (STEP_ORDER.linkedin > STEP_ORDER[prev] ? 'linkedin' : prev));
    } catch (err: unknown) {
      const message = getUserFacingError(err, 'Could not parse your resume. Please try again.');
      setError(message);
      toast.error(message);
    } finally {
      setParsing(false);
    }
  }, []);

  const onResumeFileSelected = useCallback(
    (file: File) => {
      if (!isAllowedResume(file)) {
        setError('Upload a PDF or DOCX resume.');
        return;
      }
      if (file.size > MAX_FILE_SIZE) {
        setError('Resume must be 10MB or smaller.');
        return;
      }
      setResumeSizeBytes(file.size);
      void parseResume(file);
    },
    [parseResume]
  );

  const goToReview = async () => {
    const saved = await saveDraft('review', { silent: true });
    if (saved) {
      goToStep('review');
    }
  };

  const completeOnboarding = async () => {
    if (!draft) {
      setError('Upload a resume to generate your profile draft first.');
      return;
    }
    if (hasPendingVideoUploads() || hasPendingDocumentUploads()) {
      toast.info('Please wait for media uploads to finish.');
      return;
    }
    if (hasVideoUploadErrors() || hasDocumentUploadErrors()) {
      toast.error('Fix failed media uploads before completing onboarding.');
      return;
    }

    const itemsToSave = promptItems.map((item, index) => ({
      ...item,
      fileUrl: isPersistedMediaUrl(videoUrls[index] || item.fileUrl)
        ? String(videoUrls[index] || item.fileUrl)
        : '',
      supportedDocumentUrl: isPersistedMediaUrl(documentUrls[index] || item.supportedDocumentUrl)
        ? String(documentUrls[index] || item.supportedDocumentUrl)
        : '',
    }));

    setSaving(true);
    setError('');
    try {
      const formData = buildSaveDraftFormData({
        draft,
        linkedin: linkedin.trim() ? normalizeUrl(linkedin.trim()) : '',
        targetRole,
        step: 'review',
        promptItems: itemsToSave,
        videoFiles: {},
        documentFiles: {},
        videoUrls,
        documentUrls,
      });
      formData.append('publish', 'true');
      formData.append('complete', 'true');
      const { data } = await axiosInstance.post(API_ROUTES.ONBOARDING.FINALIZE, formData);
      if (!data?.success) {
        throw new Error(data?.message || 'Could not complete onboarding.');
      }
      if (data.user) {
        syncCurrentUserFromApi(data.user as Record<string, unknown>);
      }
      clearAllVideoUploadState();
      toast.success('Onboarding complete. Welcome to SkipScreen!');
      navigate('/dashboard');
    } catch (err: unknown) {
      const message = getUserFacingError(err, 'Could not complete onboarding. Please try again.');
      setError(message);
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const handlePromptChange = (index: number, patch: Partial<ScreeningPromptItem>) => {
    setPromptItems((prev) => {
      const next = prev.map((item, itemIndex) =>
        itemIndex === index ? { ...item, ...patch } : item
      );
      promptItemsRef.current = next;
      return next;
    });
  };

  const handlePromptItemsChange = (items: ScreeningPromptItem[]) => {
    promptItemsRef.current = items;
    setPromptItems(items);
  };

  const handlePromptVideoFile = (index: number, file: File | null) => {
    if (!file) {
      clearVideoUploadState(index);
      setVideoFiles((prev) => {
        const next = { ...prev };
        delete next[index];
        return next;
      });
      handlePromptChange(index, { fileUrl: '' });
      return;
    }
    clearVideoUploadState(index);
    setVideoFiles((prev) => ({ ...prev, [index]: file }));
    handlePromptChange(index, { fileUrl: '' });
  };

  const handlePromptDocumentFile = (index: number, file: File | null) => {
    if (!file) {
      clearDocumentUploadState(index);
      setDocumentFiles((prev) => {
        const next = { ...prev };
        delete next[index];
        return next;
      });
      handlePromptChange(index, { supportedDocumentUrl: '' });
      return;
    }
    clearDocumentUploadState(index);
    setDocumentFiles((prev) => ({ ...prev, [index]: file }));
    handlePromptChange(index, { supportedDocumentUrl: '' });
  };

  if (loadingDraft) {
    return (
      <div className="fast-onboarding fast-onboarding--loading">
        <Spinner animation="border" role="status" />
        <span>Loading your onboarding progress…</span>
      </div>
    );
  }

  if (step === 'review' && draft) {
    return (
      <div className="ssr-root">
        <OnboardingNav
          step={step}
          furthestStep={furthestStep}
          onStepSelect={handleStepSelect}
          onLogout={() => logoutUser()}
        />
        {error ? (
          <div className="px-4 pt-3">
            <Alert variant="danger">{error}</Alert>
          </div>
        ) : null}
        <ReviewStage
          draft={draft}
          targetRole={targetRole}
          linkedin={linkedin}
          resumeUrl={resumeUrl}
          sessionUser={sessionUser}
          saving={saving}
          promptItems={promptItems}
          videoFiles={videoFiles}
          documentFiles={documentFiles}
          videoUrls={videoUrls}
          documentUrls={documentUrls}
          videoUploads={videoUploads}
          documentUploads={documentUploads}
          initials={initials}
          onPersistDraft={persistDraftChanges}
          onPromptChange={handlePromptChange}
          onPromptItemsChange={handlePromptItemsChange}
          onPromptVideoFile={handlePromptVideoFile}
          onPromptDocumentFile={handlePromptDocumentFile}
          onRetryVideoUpload={retryVideoUpload}
          onCancelVideoUpload={cancelVideoUpload}
          onRetryDocumentUpload={retryDocumentUpload}
          onCancelDocumentUpload={cancelDocumentUpload}
          onPersistPromptDetails={persistPromptDetails}
          onBack={() => handleStepSelect('linkedin')}
          onComplete={() => void completeOnboarding()}
        />
      </div>
    );
  }

  if (step === 'welcome') {
    return (
      <div className="ssr-root">
        <OnboardingNav
          step={step}
          furthestStep={furthestStep}
          onStepSelect={handleStepSelect}
          onLogout={() => logoutUser()}
        />
        <WelcomeStage
          firstName={String(sessionUser?.firstName || sessionUser?.fname || '')}
          onContinue={() => goToStep('resume')}
        />
      </div>
    );
  }

  if (step === 'resume') {
    return (
      <div className="ssr-root">
        <OnboardingNav
          step={step}
          furthestStep={furthestStep}
          onStepSelect={handleStepSelect}
          onLogout={() => logoutUser()}
        />
        <ResumeStage
          parsing={parsing}
          hasDraft={Boolean(draft)}
          resumeName={resumeName}
          resumeSizeLabel={formatResumeSize(resumeSizeBytes)}
          error={error}
          onSelectFile={onResumeFileSelected}
          onBack={() => handleStepSelect('welcome')}
          onContinue={() => handleStepSelect('linkedin')}
        />
      </div>
    );
  }

  if (step === 'linkedin') {
    return (
      <div className="ssr-root">
        <OnboardingNav
          step={step}
          furthestStep={furthestStep}
          onStepSelect={handleStepSelect}
          onLogout={() => logoutUser()}
        />
        {error ? (
          <div className="px-4 pt-3">
            <Alert variant="danger">{error}</Alert>
          </div>
        ) : null}
        <LinkedInStage
          draft={draft}
          linkedin={linkedin}
          resumeName={resumeName}
          resumeUrl={resumeUrl}
          targetRole={targetRole}
          initials={initials}
          saving={saving}
          sessionUser={sessionUser}
          promptItems={promptItems}
          videoFiles={videoFiles}
          onLinkedinChange={setLinkedin}
          onBack={() => handleStepSelect('resume')}
          onContinue={() => void goToReview()}
        />
      </div>
    );
  }

  return null;
}
